import $ from 'jquery'
import includes from 'lodash-es/includes'
import headerUtils from 'http-headers-validation'
import { isURL } from './helpers'
import { Forms } from './forms'
import {
  VerifyState,
  VerifyStatus,
  TriggerVerifyResult,
  anyFailed,
  applyResults,
  clearTriggers,
  emptyState,
  invalidateAll,
  markTesting,
  resultsByTrigger,
  stateFromInitialStatuses,
  statusFor,
} from './webhook-verify-state'

interface VerifyResponse {
  success?: boolean
  message?: string
  results?: TriggerVerifyResult[]
}

interface SaveResponse {
  ok?: boolean
  redirect_url?: string
  message?: string
  results?: TriggerVerifyResult[]
}

const CHIP_LABELS: Record<VerifyStatus, string> = {
  not_tested: 'Not tested',
  testing: 'Testing...',
  passed: 'Verified',
  failed: 'Not Verified',
}

const Webhooks = {
  state: emptyState() as VerifyState,
  saving: false,

  init: function (): void {
    $(document).on('submit', '.js-webhook-form', Webhooks.save)
    $(document).on('change', '.js-webhook-event', Webhooks.selectEvent)
    $(document).on('click', '.js-webhook-send-test', Webhooks.sendTest)
    $(document).on(
      'input change',
      '.js-webhook-form .js-webhook-url, .js-webhook-form .webhook-advanced_content input',
      Webhooks.invalidateVerification
    )

    Webhooks.hydrate()
  },

  hydrate: function (): void {
    const initial = $('.js-webhook-form').data('webhookVerifyInitial') as
      | Record<string, string>
      | undefined
    Webhooks.state = stateFromInitialStatuses(initial)
    Webhooks.render()
  },

  prefersReducedMotion: function (): boolean {
    return (
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false
    )
  },

  // Scroll to the top of the page to reveal validation errors, honoring the
  // OS "reduce motion" setting (jump instantly when it is on).
  scrollToTop: function (): void {
    if (Webhooks.prefersReducedMotion()) {
      $('html, body').scrollTop(0)
    } else {
      $('html, body').animate({ scrollTop: 0 })
    }
  },

  checkedParentEvents: function (): JQuery {
    return $('.webhook-event-option .js-webhook-event:checked')
  },

  selectedTriggerTypes: function (): string[] {
    return Webhooks.checkedParentEvents()
      .map(function (this: HTMLElement) {
        return $(this).data('event')
      })
      .get()
  },

  // Send only the fields the verify endpoint needs. A full form serialize would
  // also include the edit form's hidden `_method=patch`, which would reroute
  // this POST to the update member route (id="verify") and 404 it. (Save keeps
  // `_method` on purpose -- see `save` -- so its PATCH reaches `update`.)
  formData: function ($el: JQuery): string {
    return $el
      .parents('form')
      .find(
        '[name^="webhook["], [name="existing_webhook_id"], [name="authenticity_token"]'
      )
      .serialize()
  },

  displayNameForTrigger: function (triggerType: string): string {
    const text = $('.js-webhook-event')
      .filter(function (this: HTMLElement) {
        return $(this).data('event') === triggerType
      })
      .closest('.js-webhook-event-row')
      .find('.webhook-event_name')
      .first()
      .text()
      .trim()

    return text || triggerType
  },

  validateForm: function (): string[] {
    const errors: string[] = []

    function removeHeaderError($el: JQuery): void {
      $el
        .removeClass('is-invalid')
        .removeAttr('aria-invalid')
        .removeAttr('aria-describedby')
    }

    $('.js-webhook-header').each(function (this: HTMLElement) {
      const $key = $(this).find('.u-input[data-type="key"]')
      const $value = $(this).find('.u-input[data-type="value"]')

      if ($key.val() === '' && $value.val() === '') {
        removeHeaderError($key)
        removeHeaderError($value)
        return
      }

      if (
        headerUtils.validateHeader($key.val() as string, $value.val() as string)
      ) {
        removeHeaderError($key)
        removeHeaderError($value)
      } else {
        errors.push('header')
        $key.addClass('is-invalid').attr({
          'aria-invalid': 'true',
          'aria-describedby': 'webhook-header-error',
        })
        $value.addClass('is-invalid').attr({
          'aria-invalid': 'true',
          'aria-describedby': 'webhook-header-error',
        })

        if (!$('.js-webhook-header-error').length) {
          $('.js-field-manage')
            .find('.c-form-row')
            .last()
            .append(
              '<div class="c-form-row_error js-webhook-header-error" id="webhook-header-error"><p>Please enter a valid header.</p></div>'
            )
        }
      }
    })

    if (!includes(errors, 'header')) {
      $('.js-webhook-header-error').remove()
    }

    const $url = $('.js-webhook-url')
    if (isURL($url.val())) {
      Forms.removeError($url.parents('.c-form-row'))
    } else {
      errors.push('url')
      Forms.showError(
        $url.parents('.c-form-row'),
        'Please enter a valid webhook URL.'
      )
    }

    if (Webhooks.selectedTriggerTypes().length) {
      Forms.removeError($('.js-webhook-event').parents('.c-form-row'))
    } else {
      errors.push('events')
      Forms.showError(
        $('.js-webhook-event').parents('.c-form-row'),
        'Please select at least 1 event.'
      )
    }

    return errors
  },

  save: function (this: HTMLElement, event: JQuery.SubmitEvent): void {
    event.preventDefault()

    if (Webhooks.saving) {
      return
    }

    const $form = $(this)
    const errors = Webhooks.validateForm()

    if (errors.length) {
      if (
        includes(errors, 'header') &&
        !$('.webhook-advanced_content').is(':visible')
      ) {
        $('.webhook-advanced_header .js-expand').trigger('click')
      }

      Webhooks.scrollToTop()
      return
    }

    const selected = Webhooks.selectedTriggerTypes()
    const $submit = $form.find('.js-webhook-save')

    Webhooks.hideSaveError()
    // A save only verifies the checked events, so start from a clean slate and
    // mark just those testing. This drops any status left on unchecked events
    // (e.g. a prior manual Send test) so the save shows only what it attempted.
    Webhooks.state = markTesting(emptyState(), selected)
    Webhooks.render()
    Webhooks.announce('Verifying selected events...')

    // The disabled attribute would drop keyboard focus to <body>, so show the
    // loading state with a class instead and guard re-entry with Webhooks.saving.
    Webhooks.saving = true
    $submit.addClass('is-loading').attr('aria-busy', 'true')

    $.ajax({
      type: 'POST',
      url: $form.attr('action'),
      data: $form.serialize(),
      dataType: 'json',
      timeout: 30000,
    })
      .done(function (response: SaveResponse) {
        if (response && response.ok && response.redirect_url) {
          window.location.assign(response.redirect_url)
          return
        }

        Webhooks.handleSaveFailure(response, selected)
        Webhooks.clearSaving($submit)
      })
      .fail(function (xhr: JQuery.jqXHR<SaveResponse>) {
        Webhooks.handleSaveFailure(xhr.responseJSON, selected)
        Webhooks.clearSaving($submit)
      })
  },

  clearSaving: function ($submit: JQuery): void {
    Webhooks.saving = false
    $submit
      .removeClass('is-loading')
      .removeAttr('aria-busy')
      .prop('disabled', false)
  },

  handleSaveFailure: function (
    response: SaveResponse | undefined,
    selected: string[]
  ): void {
    Webhooks.state = applyResults(
      Webhooks.state,
      selected,
      resultsByTrigger(response?.results)
    )
    Webhooks.render()
    Webhooks.renderSaveError(response, selected)
  },

  sendTest: function (this: HTMLElement, event: JQuery.ClickEvent): void {
    event.preventDefault()

    const $button = $(this)
    const triggerType = $button.data('trigger') as string

    if (statusFor(Webhooks.state, triggerType) === 'testing') {
      return
    }

    const $url = $('.js-webhook-url')
    if (!isURL($url.val())) {
      Forms.showError(
        $url.parents('.c-form-row'),
        'Please enter a valid webhook URL.'
      )
      Webhooks.scrollToTop()
      return
    }

    Forms.removeError($url.parents('.c-form-row'))
    $button.addClass('is-loading').attr('aria-busy', 'true')

    Webhooks.state = markTesting(Webhooks.state, [triggerType])
    Webhooks.render()
    Webhooks.announce(
      'Testing ' + Webhooks.displayNameForTrigger(triggerType) + '...'
    )

    $.ajax({
      type: 'POST',
      url: $button.data('verifyUrl') as string,
      data:
        Webhooks.formData($button) +
        '&trigger=' +
        encodeURIComponent(triggerType),
      dataType: 'json',
      timeout: 30000,
    })
      .done(function (response: VerifyResponse) {
        const byTrigger = resultsByTrigger(response.results)

        // A single-event verify should return that event's result; if the API
        // only sent a top-level message (e.g. a config error), attach it here.
        if (!byTrigger[triggerType] && response.message) {
          byTrigger[triggerType] = {
            trigger_type: triggerType,
            success: Boolean(response.success),
            message: response.message,
          }
        }

        Webhooks.state = applyResults(Webhooks.state, [triggerType], byTrigger)
        Webhooks.render()
        Webhooks.announce(
          Webhooks.displayNameForTrigger(triggerType) +
            ': ' +
            CHIP_LABELS[statusFor(Webhooks.state, triggerType)]
        )
      })
      .fail(function () {
        Webhooks.state = clearTriggers(Webhooks.state, [triggerType])
        Webhooks.render()
        Webhooks.announce(
          "We couldn't verify " +
            Webhooks.displayNameForTrigger(triggerType) +
            '. Please try again.'
        )
      })
      .always(function () {
        $button.removeClass('is-loading').removeAttr('aria-busy')
      })
  },

  selectEvent: function (this: HTMLElement): void {
    const $input = $(this)
    const parentEvent = $input.data('event') as string | undefined

    if (parentEvent) {
      const $options = $('.js-webhook-event-option').filter(function (
        this: HTMLElement
      ) {
        return $(this).data('event') === parentEvent
      })

      if (Webhooks.prefersReducedMotion()) {
        $options.toggle()
      } else {
        $options.slideToggle(250)
      }

      $options.find('input[type="checkbox"]').prop('checked', false)

      Webhooks.state = clearTriggers(Webhooks.state, [parentEvent])
    } else {
      const owningTrigger = $input.closest('[data-event]').data('event') as
        | string
        | undefined
      if (owningTrigger) {
        Webhooks.state = clearTriggers(Webhooks.state, [owningTrigger])
      }
    }

    Webhooks.render()
  },

  // Editing the URL, headers, or auth changes what every event verifies
  // against, so drop all prior results.
  invalidateVerification: function (): void {
    Webhooks.state = invalidateAll(Webhooks.state)
    Webhooks.render()
  },

  renderChip: function ($chip: JQuery, status: VerifyStatus): void {
    $chip.removeClass(
      'webhook-verify-chip--passed webhook-verify-chip--failed webhook-verify-chip--testing'
    )

    if (status === 'passed') {
      $chip.addClass('webhook-verify-chip--passed')
    } else if (status === 'failed') {
      $chip.addClass('webhook-verify-chip--failed')
    } else if (status === 'testing') {
      $chip.addClass('webhook-verify-chip--testing')
    }

    $chip.text(CHIP_LABELS[status])
  },

  renderSaveError: function (
    response: SaveResponse | undefined,
    selected: string[]
  ): void {
    const message = anyFailed(selected, Webhooks.state)
      ? 'Webhook failed to save. See error details above.'
      : (response && response.message) ||
        'Something went wrong. Please try again.'

    $('.js-webhook-save-error-message').text(message)
    $('.js-webhook-save-error')
      .show()
      .attr('aria-hidden', 'false')
      .trigger('focus')
  },

  hideSaveError: function (): void {
    $('.js-webhook-save-error').hide().attr('aria-hidden', 'true')
  },

  renderEventError: function ($error: JQuery, triggerType: string): void {
    const triggerState = Webhooks.state[triggerType]
    const $input = $('.js-webhook-event').filter(function (this: HTMLElement) {
      return $(this).data('event') === triggerType
    })

    if (!triggerState || triggerState.status !== 'failed') {
      $error.text('').attr('hidden', 'hidden')
      $input.removeAttr('aria-invalid').removeAttr('aria-describedby')
      return
    }

    $error
      .text(triggerState.message || 'Verification failed.')
      .removeAttr('hidden')
    $input.attr({
      'aria-invalid': 'true',
      'aria-describedby': $error.attr('id') as string,
    })
  },

  // Announce chip status changes through the single polite live region. Chips
  // do not carry their own aria-live, so this is the one place SR users hear
  // per-event verification updates.
  announce: function (message: string): void {
    $('.js-webhook-status').text(message)
  },

  render: function (): void {
    $('.js-verify-chip').each(function (this: HTMLElement) {
      const $chip = $(this)
      Webhooks.renderChip(
        $chip,
        statusFor(Webhooks.state, $chip.data('trigger'))
      )
    })

    $('.js-webhook-event-error').each(function (this: HTMLElement) {
      const $error = $(this)
      Webhooks.renderEventError($error, $error.data('trigger'))
    })
  },
}

$(Webhooks.init)
